import { type ComponentProps, type PropsWithChildren, forwardRef, useMemo } from 'react'
import { tv } from 'tailwind-variants'

import { Checkbox, type Props as CheckboxProps } from '../Checkbox'
import { VisuallyHiddenText } from '../VisuallyHiddenText'

import { Td } from './Td'

type AbstractProps = PropsWithChildren<{
  /** Checkboxのaccessible nameとして設定するテキストを参照するためのid属性値。同じ親Tr配下のTdかTh、もしくはその子孫要素のidを指定する。複数要素のテキストを指定する場合は空白区切りでidをつなぐ */
  'aria-labelledby': string
}> &
  Pick<ComponentProps<typeof Td>, 'vAlign' | 'fixed'>
type Props = Omit<CheckboxProps, keyof AbstractProps> & AbstractProps

const classNameGenerator = tv({
  slots: {
    inner: [
      'shr-relative',
      'shr-flex shr-justify-center shr-px-1 shr-py-0.75',
      '[&:not(:has([disabled]))]:shr-cursor-pointer',
    ],
    wrapper: 'shr-w-min shr-p-0',
    checkbox: ['shr-leading-[0]', '[&>span]:shr-translate-y-[unset]'],
  },
})

export const TdCheckbox = forwardRef<HTMLInputElement, Props>(
  ({ vAlign, fixed, children, className, ...rest }, ref) => {
    const classNames = useMemo(() => {
      const { wrapper, inner, checkbox } = classNameGenerator()

      return {
        wrapper: wrapper({ className }),
        inner: inner(),
        checkbox: checkbox(),
      }
    }, [className])

    return (
      // Td に必要な属性やイベントは不要
      <Td vAlign={vAlign} fixed={fixed} className={classNames.wrapper}>
        <label className={classNames.inner}>
          {/* eslint-disable-next-line smarthr/a11y-prohibit-checkbox-or-radio-in-table-cell */}
          <Checkbox {...rest} ref={ref} className={classNames.checkbox} />
          {children && <VisuallyHiddenText>{children}</VisuallyHiddenText>}
        </label>
      </Td>
    )
  },
)
